import React from "react";
import { isAllBusiness } from "@/api";
import { UsersSettingsTab, UsersSettingsTabs, TabIcons, TabLabels } from "./tabs";
import { useAuth } from "@/Auth";
import { useGetInactiveFoodicsAccounts } from "@/hooks/useGetInactiveFoodicsAccounts";
import { mergeUserEmails } from "@/utils/mergeUserEmails";
import { useGetUsers } from "@/hooks/useGetUsers";
import { useGetBusinesses } from "@/hooks/useGetBusinesses";
import { BusinessOption, StatusOption, UserFilters } from "./UserFilters";
import { UserContent } from "./UserContent";
import type { UserAggregate1 } from "@norma-bi/bi-api";
import { useBusinessesStatusGetter } from "@/hooks/useGetBusinessStatus";
import { OrganizationSyncControl } from "./OrganizationSyncControl";
import { SettingsPageHeader } from "../components/SettingsPageHeader";
import { useT } from "@/i18n";

export function SettingsUsers() {
  const auth = useAuth();
  const userIsDemo = auth.user?.is_demo ?? false;
  const businesses = useGetBusinesses();
  const getBusinessStatus = useBusinessesStatusGetter();
  const { t } = useT();

  const [selectedTab, setSelectedTab] = React.useState<UsersSettingsTab>(UsersSettingsTabs.active);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [selectedBusinessId, setSelectedBusinessId] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    if (
      businesses.data?.length &&
      !businesses.data.some((business) => business.id === selectedBusinessId)
    ) {
      setSelectedBusinessId(businesses.data[0].id);
    }
  }, [selectedBusinessId, businesses.data]);

  const usersQuery = useGetUsers({
    businessId: selectedBusinessId,
    userIsDemo,
    enabled: selectedTab === UsersSettingsTabs.active && !!selectedBusinessId,
  });

  const inactiveFoodicsAccountsQuery = useGetInactiveFoodicsAccounts({
    businessId: selectedBusinessId || "",
    userIsDemo,
    enabled:
      selectedTab === UsersSettingsTabs.inactive &&
      !!selectedBusinessId &&
      !isAllBusiness(selectedBusinessId),
  });

  const users = React.useMemo(() => {
    let users: UserAggregate1[] = [];
    if (usersQuery.isSuccess) {
      users = usersQuery.data || [];
    }
    if (searchTerm !== "") {
      users = users.filter((u) =>
        mergeUserEmails(u).toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }
    return users.map((u) => {
      return {
        id: u.id,
        name: u.name,
        email: mergeUserEmails(u),
        branchesCount: u.venues_count,
        businessesCount: u.businesses_count,
        is_root: u.is_root,
      };
    });
  }, [usersQuery.isSuccess, usersQuery.data, searchTerm]);

  const inactiveFoodicsAccounts = React.useMemo(() => {
    if (!inactiveFoodicsAccountsQuery.isSuccess) return [];

    const acc = inactiveFoodicsAccountsQuery.data.filter((a) => {
      return a.email.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const mappedAccounts = acc.map((a) => {
      return {
        id: a.id,
        name: a.name,
        email: a.email,
        branchesCount: a.branches?.length ?? 0,
        roles: [...(a.is_owner ? [t("users.owner")] : []), ...(a.roles?.map((r) => r.name) ?? [])],
      };
    });

    return mappedAccounts.sort((a, b) => {
      const countA = a.branchesCount || 0;
      const countB = b.branchesCount || 0;
      return countB - countA;
    });
  }, [inactiveFoodicsAccountsQuery.isSuccess, inactiveFoodicsAccountsQuery.data, searchTerm, t]);

  const tabs = React.useMemo(() => [UsersSettingsTabs.active, UsersSettingsTabs.inactive], []);

  const statusOptions = React.useMemo<StatusOption[]>(() => {
    return tabs.map((tab) => ({
      id: tab,
      label: t(TabLabels[tab]),
      icon: TabIcons[tab],
    }));
  }, [tabs, t]);

  const businessOptions = React.useMemo<BusinessOption[]>(() => {
    if (!businesses.isSuccess) return [];

    return businesses.data.map((business) => {
      const status = getBusinessStatus(business);
      return {
        id: business.id,
        label: business.display_name,
        disabled: status === "unlicensed" || status === "onboarding",
        metadata:
          status === "onboarding" ? (
            <span className="ms-auto text-xs text-muted-foreground">{t("users.pending")}</span>
          ) : status === "unlicensed" ? (
            <span className="ms-auto text-xs text-muted-foreground">{t("users.unlicensed")}</span>
          ) : undefined,
      };
    });
  }, [businesses.isSuccess, businesses.data, getBusinessStatus, t]);

  function onSelectBusiness(businessId: string) {
    setSelectedBusinessId(businessId);
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <SettingsPageHeader
        title={t("settings.users")}
        description={
          selectedTab === UsersSettingsTabs.active
            ? t("users.activeDescription")
            : t("users.inactiveDescription")
        }
      >
        <OrganizationSyncControl
          businessId={selectedBusinessId}
          userIsDemo={userIsDemo}
          inactiveTab={selectedTab === UsersSettingsTabs.inactive}
        />
      </SettingsPageHeader>
      <UserFilters
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        statusOptions={statusOptions}
        businessOptions={businessOptions}
        selectedBusinessId={selectedBusinessId}
        onSelectBusiness={onSelectBusiness}
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
      />
      <UserContent
        selectedTab={selectedTab}
        businessId={selectedBusinessId}
        users={users}
        inactiveFoodicsAccounts={inactiveFoodicsAccounts}
        usersQuery={usersQuery}
        inactiveFoodicsAccountsQuery={inactiveFoodicsAccountsQuery}
      />
    </div>
  );
}
